<script setup>
import { ref, useTemplateRef, onMounted, onUnmounted } from 'vue';
import { debounce } from '@chatwoot/utils';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from 'next/polaris/ui/input-group';
import RecentSearches from './RecentSearches.vue';

const emit = defineEmits(['search', 'selectRecentSearch']);

const searchQuery = defineModel({
  type: String,
  default: '',
});
const isInputFocused = ref(false);
const showRecentSearches = ref(false);
const searchInput = useTemplateRef('searchInput');
const recentSearchesRef = useTemplateRef('recentSearchesRef');

// The kit's InputGroupInput is a component: the native <input> is its root element.
const focusInput = () => searchInput.value?.$el?.focus();
const blurInput = () => searchInput.value?.$el?.blur();

const handler = e => {
  if (e.key === '/' && document.activeElement.tagName !== 'INPUT') {
    e.preventDefault();
    focusInput();
  } else if (e.key === 'Escape' && document.activeElement.tagName === 'INPUT') {
    e.preventDefault();
    blurInput();
  }
};

const debouncedEmit = debounce(
  value =>
    emit('search', value.length > 1 || value.match(/^[0-9]+$/) ? value : ''),
  500
);

const onInput = e => {
  // Use the DOM value, not searchQuery.value: the defineModel ref updates a tick
  // later, so reading it back here lags one character behind.
  const value = e.target.value;
  debouncedEmit(value);

  if (value.trim()) {
    showRecentSearches.value = false;
  } else if (isInputFocused.value) {
    showRecentSearches.value = true;
  }
};

const onFocus = () => {
  isInputFocused.value = true;
  if (!searchQuery.value.trim()) {
    showRecentSearches.value = true;
  }
};

const onBlur = () => {
  isInputFocused.value = false;
  showRecentSearches.value = false;
};

const onSelectRecentSearch = query => {
  searchQuery.value = query;
  emit('selectRecentSearch', query);
  showRecentSearches.value = false;
  focusInput();
};

const addToRecentSearches = query => {
  if (recentSearchesRef.value) {
    recentSearchesRef.value.addRecentSearch(query);
  }
};

defineExpose({
  addToRecentSearches,
});

onMounted(() => {
  focusInput();
  document.addEventListener('keydown', handler);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handler);
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col w-full">
      <InputGroup class="!h-11">
        <InputGroupAddon>
          <Icon icon="i-lucide-search" class="size-4" aria-hidden="true" />
        </InputGroupAddon>
        <InputGroupInput
          ref="searchInput"
          v-model="searchQuery"
          type="search"
          class="reset-base h-full !m-0 text-polaris-foreground"
          :placeholder="$t('SEARCH.INPUT_PLACEHOLDER')"
          :aria-label="$t('SEARCH.INPUT_PLACEHOLDER')"
          @focus="onFocus"
          @blur="onBlur"
          @input="onInput"
        />
        <InputGroupAddon align="inline-end" class="hidden sm:flex">
          <span class="type-polaris-caption">
            {{ $t('SEARCH.PLACEHOLDER_KEYBINDING') }}
          </span>
        </InputGroupAddon>
      </InputGroup>

      <div
        class="transition-all duration-200 ease-out grid overflow-hidden w-full"
        :class="
          showRecentSearches
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0'
        "
      >
        <div class="overflow-hidden w-full">
          <RecentSearches
            ref="recentSearchesRef"
            @select-search="onSelectRecentSearch"
            @clear-all="showRecentSearches = false"
          />
        </div>
      </div>
    </div>

    <slot />
  </div>
</template>
